import { getMessaging, getToken, onMessage, MessagePayload } from 'firebase/messaging';
import app from '../config/firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let messagingInstance: ReturnType<typeof getMessaging> | null = null;

function getMessagingInstance() {
  if (!messagingInstance) {
    messagingInstance = getMessaging(app);
  }
  return messagingInstance;
}

export type PermissionResult =
  | { ok: true; token: string }
  | { ok: false; reason: 'denied' | 'vapid' | 'token_error'; detail?: string };

/**
 * Request notification permission and get FCM token.
 * Returns a discriminated result so the caller can show an accurate message.
 */
export async function requestNotificationPermission(): Promise<PermissionResult> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { ok: false, reason: 'denied' };
    }

    if (!VAPID_KEY) {
      console.warn('VITE_FIREBASE_VAPID_KEY not set — cannot get FCM token');
      return { ok: false, reason: 'vapid' };
    }

    const messaging = getMessagingInstance();
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js'),
    });

    if (!token) {
      return { ok: false, reason: 'token_error', detail: 'Empty token returned' };
    }

    return { ok: true, token };
  } catch (error: any) {
    console.error('Error getting notification permission/token:', error);

    // If permission was already granted but token failed, don't blame the browser
    if (Notification.permission === 'granted') {
      return { ok: false, reason: 'token_error', detail: error?.message };
    }

    return { ok: false, reason: 'denied' };
  }
}

/**
 * Listen for foreground messages and invoke callback.
 */
export function onForegroundMessage(callback: (payload: MessagePayload) => void): () => void {
  const messaging = getMessagingInstance();
  return onMessage(messaging, callback);
}
